import { localize, siteContent, type Locale, type LocalizedText } from "@/content/site";

export type ProofBarItem = {
  code: string;
  label: string;
  value: string;
};

export type TrustPillarItem = {
  title: LocalizedText;
  desc: LocalizedText;
};

export type MethodologyStep = {
  title: LocalizedText;
  desc: LocalizedText;
};

export type WorkEngagement = {
  context: LocalizedText;
  startingCondition: LocalizedText;
  mandate: LocalizedText;
  deliveredCapability: LocalizedText;
  dataAndIntegrations: readonly LocalizedText[];
  controls: readonly LocalizedText[];
  measurementFrame: readonly LocalizedText[];
  relatedService: {
    href: string;
    label: LocalizedText;
  };
};

export type WorkCtaPath = {
  id: "contact" | "verification";
  href: string;
  title: LocalizedText;
  desc: LocalizedText;
  label: LocalizedText;
};

export const proofTrustCopy = {
  work: {
    heroLabel: {
      en: "Selected production work",
      fr: "Mandats sélectionnés en production",
    },
    heroTitle: {
      en: "Selected production AI engagements—from operating problem to controlled delivery.",
      fr: "Des mandats d’IA en production, du problème opérationnel à une livraison contrôlée.",
    },
    heroSubtitle: {
      en: "Four Vert AI engagements show the operating challenge, the mandate, the capability delivered, and the production discipline around the work.",
      fr: "Quatre mandats de Vert AI présentent le défi opérationnel, le mandat confié, la capacité livrée et la discipline de production qui encadre le travail.",
    },
    heroSecondaryCta: {
      en: "Review the engagements",
      fr: "Voir les mandats",
    },
    evidenceLabel: {
      en: "Evidence note",
      fr: "Note sur les preuves",
    },
    evidenceTitle: {
      en: "Real engagements, shared without confidential client details.",
      fr: "Des mandats réels, présentés sans divulguer de renseignements confidentiels sur les clients.",
    },
    evidenceDisclosure: {
      en: "These four profiles describe real Vert AI engagements. Client identities, confidential system details, and quantified outcomes are omitted because they are confidential or have not been approved for public use. Each profile therefore focuses on the operating context, capability delivered, production controls, and measurement frame.",
      fr: "Ces quatre profils décrivent de vrais mandats de Vert AI. L’identité des clients, les détails confidentiels des systèmes et les résultats chiffrés sont omis parce qu’ils sont confidentiels ou que leur diffusion n’a pas été autorisée. Chaque profil se limite donc au contexte opérationnel, à la capacité livrée, aux contrôles de production et au cadre de mesure.",
    },
    patternsEyebrow: {
      en: "Engagement records",
      fr: "Dossiers de mandat",
    },
    patternsTitle: {
      en: "What Vert AI changed, built, and put under control.",
      fr: "Ce que Vert AI a modifié, construit et placé sous contrôle.",
    },
    patternsIntro: {
      en: "Each record answers the same practical questions: what made the work costly, what Vert AI was asked to change, what capability was delivered, and how the work was controlled and prepared for measurement.",
      fr: "Chaque dossier répond aux mêmes questions concrètes : ce qui rendait le travail coûteux, ce que Vert AI devait modifier, la capacité livrée, ainsi que la façon dont le travail a été contrôlé et préparé pour la mesure.",
    },
    engagementTitles: {
      "spreadsheet-reporting-to-decision-system": {
        en: "From spreadsheet reporting to a governed decision system",
        fr: "Des rapports sur tableurs à un système décisionnel gouverné",
      },
      "manual-triage-to-human-reviewed-ai": {
        en: "From manual request triage to a human-reviewed AI workflow",
        fr: "Du triage manuel des demandes à un processus assisté par l’IA avec validation humaine",
      },
      "fragmented-data-to-ai-ready-foundation": {
        en: "From fragmented operational data to an AI-ready foundation",
        fr: "De données opérationnelles fragmentées à une fondation prête pour l’IA",
      },
      "prototype-to-controlled-production-workflow": {
        en: "From an AI prototype to a controlled production workflow",
        fr: "D’un prototype d’IA à un processus opérationnel contrôlé en production",
      },
    },
    labels: {
      engagement: { en: "Real engagement", fr: "Mandat réel" },
      context: { en: "Operating context", fr: "Contexte opérationnel" },
      startingCondition: { en: "Starting condition", fr: "Situation de départ" },
      mandate: { en: "Vert AI mandate", fr: "Mandat de Vert AI" },
      deliveredCapability: { en: "Capability delivered", fr: "Capacité livrée" },
      technicalRecord: { en: "Context, data, and controls", fr: "Contexte, données et contrôles" },
      dataAndIntegrations: { en: "Data and integrations", fr: "Données et intégrations" },
      controls: { en: "Production controls", fr: "Contrôles de production" },
      measurementFrame: { en: "Measurement frame", fr: "Cadre de mesure" },
      relatedService: { en: "Related service", fr: "Service connexe" },
    },
    methodologyEyebrow: {
      en: "Production method",
      fr: "Méthode de production",
    },
    methodologyTitle: {
      en: "How Vert AI builds for production.",
      fr: "Comment Vert AI conçoit et déploie des systèmes en production.",
    },
    methodologyIntro: {
      en: "The delivery sequence and the control layer stay connected. Workflow boundaries, data, human authority, evaluation, monitoring, ownership, and value measurement are designed together—not added after a model appears to work.",
      fr: "La séquence de livraison et la couche de contrôle restent liées. Les limites du processus, les données, l’autorité humaine, l’évaluation, la surveillance, les responsabilités et la mesure de la valeur sont conçues ensemble, plutôt qu’ajoutées après qu’un modèle semble fonctionner.",
    },
    methodologyFooter: {
      en: "Authority and scope expand only after the operating evidence supports the next step.",
      fr: "L’autorité et la portée s’élargissent seulement lorsque les preuves opérationnelles soutiennent l’étape suivante.",
    },
    trustEyebrow: {
      en: "Seven production controls",
      fr: "Sept contrôles de production",
    },
    trustTitle: {
      en: "The operating layer remains inspectable.",
      fr: "La couche opérationnelle demeure vérifiable.",
    },
    trustIntro: {
      en: "The exact implementation changes with the mandate. The control questions do not.",
      fr: "La mise en œuvre exacte varie selon le mandat. Les questions de contrôle demeurent les mêmes.",
    },
    founderEyebrow: {
      en: "Accountability",
      fr: "Responsabilité",
    },
    founderTitle: {
      en: "Senior judgment stays close to the production decision.",
      fr: "Le jugement au plus haut niveau reste proche de la décision de production.",
    },
    ctaEyebrow: {
      en: "Choose the right next step",
      fr: "Choisir la bonne prochaine étape",
    },
    ctaHeadline: {
      en: "Match the next conversation to the work in front of you.",
      fr: "Adaptez la prochaine discussion au travail qui vous attend.",
    },
    ctaDesc: {
      en: "A costly workflow and a live AI initiative require different first decisions. Start with the path that matches the current operating risk.",
      fr: "Un processus coûteux et une initiative d’IA déjà en cours exigent des premières décisions différentes. Commencez par le parcours qui correspond au risque opérationnel actuel.",
    },
  },
} as const;

export const methodologySteps = [
  {
    title: { en: "Define the work and its baseline", fr: "Définir le travail et sa mesure de référence" },
    desc: {
      en: "Set the workflow boundary, accountable owner, current effort, exceptions, risk, and business cost before choosing the mechanism.",
      fr: "Définir les limites du processus, la personne responsable, l’effort actuel, les exceptions, le risque et le coût opérationnel avant de choisir le mécanisme.",
    },
  },
  {
    title: { en: "Prepare data and human authority", fr: "Préparer les données et l’autorité humaine" },
    desc: {
      en: "Map sources, definitions, quality gaps, permissions, decision rights, review paths, and escalation rules.",
      fr: "Cartographier les sources, les définitions, les écarts de qualité, les autorisations, les droits de décision, les parcours de validation et les règles de transfert.",
    },
  },
  {
    title: { en: "Build and evaluate the smallest useful system", fr: "Construire et évaluer le plus petit système utile" },
    desc: {
      en: "Integrate the capability into the real workflow and test expected behavior, edge cases, failure modes, and acceptance criteria.",
      fr: "Intégrer la capacité au processus réel et tester le comportement attendu, les cas limites, les modes d’échec et les critères d’acceptation.",
    },
  },
  {
    title: { en: "Launch with ownership and observability", fr: "Mettre en service avec responsabilité et observabilité" },
    desc: {
      en: "Monitor usage, quality, exceptions, latency, incidents, adoption, and business signals; expand only after the controls hold.",
      fr: "Surveiller l’usage, la qualité, les exceptions, la latence, les incidents, l’adoption et les indicateurs d’affaires; élargir seulement lorsque les contrôles tiennent.",
    },
  },
] as const satisfies readonly MethodologyStep[];

export const trustPillars = [
  {
    title: { en: "Evaluation", fr: "Évaluation" },
    desc: {
      en: "Expected behavior, representative examples, edge cases, failure modes, and acceptance criteria are defined before release.",
      fr: "Le comportement attendu, les exemples représentatifs, les cas limites, les modes d’échec et les critères d’acceptation sont définis avant la mise en service.",
    },
  },
  {
    title: { en: "Observability", fr: "Observabilité" },
    desc: {
      en: "Usage, quality, latency, exceptions, drift, incidents, and business signals remain visible after launch.",
      fr: "L’usage, la qualité, la latence, les exceptions, la dérive, les incidents et les indicateurs d’affaires restent visibles après la mise en service.",
    },
  },
  {
    title: { en: "Human review", fr: "Validation humaine" },
    desc: {
      en: "People retain authority when risk, judgment, customer impact, or commercial accountability matter.",
      fr: "Les personnes conservent l’autorité lorsque le risque, le jugement, l’incidence sur la clientèle ou l’imputabilité commerciale comptent.",
    },
  },
  {
    title: { en: "Governance", fr: "Gouvernance" },
    desc: {
      en: "Ownership, documentation, change control, escalation, and review cadence are part of the operating design.",
      fr: "Les responsabilités, la documentation, le contrôle des changements, les transferts et la cadence d’examen font partie de la conception opérationnelle.",
    },
  },
  {
    title: { en: "Data quality", fr: "Qualité des données" },
    desc: {
      en: "Source definitions, transformations, freshness, ownership, permissions, and missing data remain traceable.",
      fr: "Les définitions des sources, les transformations, la fraîcheur, les responsabilités, les autorisations et les données manquantes restent traçables.",
    },
  },
  {
    title: { en: "Security and privacy", fr: "Sécurité et confidentialité" },
    desc: {
      en: "Access, sensitive data handling, security review, and vendor exposure are scoped before automation expands.",
      fr: "L’accès, le traitement des données sensibles, l’examen de sécurité et l’exposition aux fournisseurs sont cadrés avant d’élargir l’automatisation.",
    },
  },
  {
    title: { en: "Value measurement", fr: "Mesure de la valeur" },
    desc: {
      en: "Baseline measures, adoption, cycle time, quality, and operating impact are defined before scale decisions.",
      fr: "Les mesures de référence, l’adoption, le temps de cycle, la qualité et l’impact opérationnel sont définis avant toute décision d’expansion.",
    },
  },
] as const satisfies readonly TrustPillarItem[];

export const workEngagements = {
  "spreadsheet-reporting-to-decision-system": {
    context: {
      en: "A leadership team relied on recurring spreadsheets, manual reconciliations, and meeting-by-meeting interpretation to decide where to act.",
      fr: "Une équipe de direction dépendait de tableurs récurrents, de rapprochements manuels et d’interprétations faites en réunion pour décider où agir.",
    },
    startingCondition: {
      en: "The numbers existed, but metric definitions, decision rules, exceptions, owners, and next actions were not maintained in one reliable operating layer.",
      fr: "Les chiffres existaient, mais les définitions, les règles décisionnelles, les exceptions, les responsables et les prochaines actions n’étaient pas réunis dans une couche fiable.",
    },
    mandate: {
      en: "Vert AI was engaged to convert one recurring leadership review into a governed decision workflow and reduce its dependence on manual interpretation.",
      fr: "Vert AI a reçu le mandat de transformer un examen récurrent de la direction en processus décisionnel gouverné et de réduire sa dépendance à l’interprétation manuelle.",
    },
    deliveredCapability: {
      en: "Vert AI delivered an authoritative data model, a metric dictionary, exception logic, and a decision interface for the operating team.",
      fr: "Vert AI a livré un modèle de données de référence, un dictionnaire de mesures, une logique d’exception et une interface décisionnelle pour l’équipe opérationnelle.",
    },
    dataAndIntegrations: [
      { en: "Reporting spreadsheets and source-system extracts", fr: "Tableurs de production de rapports et extractions des systèmes sources" },
      { en: "Approved metric definitions and business rules", fr: "Définitions de mesures et règles d’affaires approuvées" },
      { en: "Decision cadence, accountable roles, and escalation paths", fr: "Cadence des décisions, rôles responsables et parcours de transfert" },
    ],
    controls: [
      { en: "Metric dictionary and lineage validation", fr: "Dictionnaire de mesures et validation du lignage" },
      { en: "Exception thresholds and review workflow", fr: "Seuils d’exception et processus de validation" },
      { en: "Change log for definitions and decision rules", fr: "Journal des changements apportés aux définitions et aux règles décisionnelles" },
    ],
    measurementFrame: [
      { en: "Reporting cycle time", fr: "Temps de cycle des rapports" },
      { en: "Reconciliation effort", fr: "Effort de rapprochement" },
      { en: "Report-to-decision latency", fr: "Délai du rapport à la décision" },
    ],
    relatedService: {
      href: "/services/decision-intelligence",
      label: { en: "Explore decision intelligence systems", fr: "Explorer les systèmes d’intelligence décisionnelle" },
    },
  },
  "manual-triage-to-human-reviewed-ai": {
    context: {
      en: "A service and operations team spent substantial time classifying requests, gathering context, drafting responses, and routing exceptions.",
      fr: "Une équipe du service à la clientèle et des opérations consacrait beaucoup de temps à classer les demandes, à rassembler le contexte, à rédiger des réponses et à acheminer les exceptions.",
    },
    startingCondition: {
      en: "Classification, context gathering, draft responses, and escalation depended on individual judgment and repeated copy-and-paste work.",
      fr: "Le classement, la collecte du contexte, les réponses provisoires et les transferts dépendaient du jugement individuel et de copier-coller répétés.",
    },
    mandate: {
      en: "Vert AI was engaged to redesign one queue as a controlled AI-assisted workflow while keeping people responsible for uncertain or higher-risk cases.",
      fr: "Vert AI a reçu le mandat de repenser une file sous forme de processus contrôlé et assisté par l’IA, tout en maintenant une responsabilité humaine pour les cas incertains ou plus risqués.",
    },
    deliveredCapability: {
      en: "Vert AI delivered a controlled triage assistant for one queue: classification, approved-context retrieval, recommended next steps, human review for uncertain cases, and quality reporting.",
      fr: "Vert AI a livré un assistant de triage contrôlé pour une file : classement, recherche de contexte approuvé, prochaines étapes proposées, validation humaine des cas incertains et suivi de la qualité.",
    },
    dataAndIntegrations: [
      { en: "Historical requests, emails, chats, and intake records", fr: "Demandes, courriels, clavardages et dossiers de réception historiques" },
      { en: "Approved knowledge, policies, and escalation rules", fr: "Connaissances, politiques et règles de transfert approuvées" },
      { en: "Queue categories, priorities, resolutions, and review notes", fr: "Catégories de files, priorités, résolutions et notes de validation" },
    ],
    controls: [
      { en: "Confidence thresholds and human-review queues", fr: "Seuils de confiance et files de validation humaine" },
      { en: "Audit trail for classifications and suggested actions", fr: "Piste d’audit pour les classements et les actions proposées" },
      { en: "Approved-source retrieval and response boundaries", fr: "Recherche dans les sources approuvées et limites de réponse" },
    ],
    measurementFrame: [
      { en: "Response and resolution time", fr: "Temps de réponse et de résolution" },
      { en: "Escalation and rework", fr: "Transferts et reprises" },
      { en: "Review effort per request", fr: "Effort de validation par demande" },
    ],
    relatedService: {
      href: "/services/production-ai-systems",
      label: { en: "Explore production AI systems", fr: "Explorer les systèmes d’IA en production" },
    },
  },
  "fragmented-data-to-ai-ready-foundation": {
    context: {
      en: "Teams wanted to use AI and improve analytics, but operational data was distributed across tools, exports, spreadsheets, and inconsistent definitions.",
      fr: "Des équipes voulaient utiliser l’IA et améliorer leurs analyses, mais les données opérationnelles étaient réparties entre des outils, des exportations, des tableurs et des définitions incohérentes.",
    },
    startingCondition: {
      en: "Models, dashboards, and automations were not reliable: the data was fragmented, stale, ambiguous, or without a clearly accountable owner.",
      fr: "Les modèles, les tableaux de bord et les automatisations n’étaient pas fiables : les données étaient fragmentées, périmées, ambiguës ou sans responsable clair.",
    },
    mandate: {
      en: "Vert AI was engaged to establish the governed data path required to support one AI-enabled workflow or recurring decision.",
      fr: "Vert AI a reçu le mandat d’établir le parcours de données gouverné nécessaire à un processus assisté par l’IA ou à une décision récurrente.",
    },
    deliveredCapability: {
      en: "Vert AI delivered a governed data model: mapped sources, core entities, quality checks, semantic definitions, access rules, and documentation.",
      fr: "Vert AI a livré un modèle de données gouverné : sources cartographiées, entités principales, contrôles de qualité, définitions sémantiques, règles d’accès et documentation.",
    },
    dataAndIntegrations: [
      { en: "Operational systems, exports, and reporting tables", fr: "Systèmes opérationnels, exportations et tables de production de rapports" },
      { en: "Entity definitions, identifiers, and accountability rules", fr: "Définitions d’entités, identifiants et règles de responsabilité" },
      { en: "Freshness, completeness, and quality expectations", fr: "Attentes de fraîcheur, d’exhaustivité et de qualité" },
    ],
    controls: [
      { en: "Data-quality tests and freshness monitoring", fr: "Tests de qualité des données et surveillance de la fraîcheur" },
      { en: "Access rules for sensitive or customer data", fr: "Règles d’accès aux données sensibles ou aux données de la clientèle" },
      { en: "Documented semantic layer and accountability model", fr: "Couche sémantique et modèle de responsabilité documentés" },
    ],
    measurementFrame: [
      { en: "Data defects and reconciliation", fr: "Défauts et rapprochements de données" },
      { en: "Time to trusted datasets", fr: "Délai de production de données fiables" },
      { en: "Inputs with clear ownership", fr: "Intrants avec responsabilité claire" },
    ],
    relatedService: {
      href: "/services/data-platforms",
      label: { en: "Explore Data Platforms for AI", fr: "Explorer les plateformes de données pour l’IA" },
    },
  },
  "prototype-to-controlled-production-workflow": {
    context: {
      en: "An AI prototype existed, but it lacked the controls, integrations, evaluation, and ownership required for dependable daily use.",
      fr: "Un prototype d’IA existait, mais il lui manquait les contrôles, les intégrations, l’évaluation et les responsabilités nécessaires à une utilisation quotidienne fiable.",
    },
    startingCondition: {
      en: "The demonstration did not cover edge cases, review queues, changing data, fallback behavior, or the operational handoff.",
      fr: "La démonstration ne couvrait pas les cas limites, les files de validation, l’évolution des données, le repli ni la prise en charge opérationnelle.",
    },
    mandate: {
      en: "Vert AI was engaged to rebuild one workflow around the prototype so that evaluation, integration, human review, fallback, and operational ownership were defined before launch.",
      fr: "Vert AI a reçu le mandat de reconstruire un processus autour du prototype afin que l’évaluation, l’intégration, la validation humaine, les mécanismes de repli et la responsabilité opérationnelle soient définis avant la mise en service.",
    },
    deliveredCapability: {
      en: "Vert AI delivered a production-readiness rebuild: evaluation harness, integration plan, monitoring, human review, fallback behavior, and launch criteria.",
      fr: "Vert AI a livré une remise à niveau pour la production : grille d’évaluation, plan d’intégration, surveillance, validation humaine, mécanismes de repli et critères de mise en service.",
    },
    dataAndIntegrations: [
      { en: "Prototype instructions, model behavior, and test examples", fr: "Instructions du prototype, comportement du modèle et exemples de test" },
      { en: "Real workflow inputs, outputs, and exception cases", fr: "Intrants, résultats produits et cas d’exception du processus réel" },
      { en: "User roles, approval paths, and launch constraints", fr: "Rôles des utilisateurs, parcours d’approbation et contraintes de mise en service" },
    ],
    controls: [
      { en: "Evaluation harness and launch acceptance criteria", fr: "Grille d’évaluation et critères d’acceptation à la mise en service" },
      { en: "Monitoring for quality, usage, latency, and failures", fr: "Surveillance de la qualité, de l’usage, de la latence et des défaillances" },
      { en: "Fallback logic, ownership, and operating review cadence", fr: "Logique de repli, responsabilités et cadence d’examen opérationnel" },
    ],
    measurementFrame: [
      { en: "Performance on approved examples", fr: "Performance sur les exemples approuvés" },
      { en: "Human-review effort and overrides", fr: "Effort de validation et modifications" },
      { en: "Adoption and production incidents", fr: "Adoption et incidents en production" },
    ],
    relatedService: {
      href: "/services/production-ai-systems",
      label: { en: "Explore production AI systems", fr: "Explorer les systèmes d’IA en production" },
    },
  },
} as const satisfies Record<string, WorkEngagement>;

export const workCtaPaths = [
  {
    id: "contact",
    href: "/contact",
    title: { en: "Build or improve a production AI system", fr: "Construire ou améliorer un système d’IA en production" },
    desc: {
      en: "You have a costly process, recurring decision, data bottleneck, or prototype that needs a credible path to production.",
      fr: "Vous avez un processus coûteux, une décision récurrente, un blocage de données ou un prototype qui exige un parcours crédible vers la production.",
    },
    label: {
      en: "Discuss a production AI opportunity",
      fr: "Discuter d’un projet d’IA en production",
    },
  },
  {
    id: "verification",
    href: "/services/ai-verification-review",
    title: {
      en: "AI Verification & Production Readiness Review",
      fr: "Examen de vérification de l’IA et de préparation à la production",
    },
    desc: {
      en: "Leadership must decide whether to fund, launch, expand, pause, stop, or reframe one initiative already in motion.",
      fr: "La direction doit décider de financer, mettre en production, élargir, suspendre, arrêter ou recadrer une initiative déjà en cours.",
    },
    label: { en: "Explore the AI Verification Review", fr: "Explorer l’examen de vérification de l’IA" },
  },
] as const satisfies readonly WorkCtaPath[];

export const getDefaultProofBarItems = (locale: Locale): ProofBarItem[] => [
  {
    code: "01",
    label: locale === "fr" ? "Fondateur" : "Founder-led",
    value: localize(siteContent.proof.founderLedDelivery, locale),
  },
  {
    code: "02",
    label: locale === "fr" ? "Expérience" : "Experience",
    value: localize(siteContent.proof.selectedOperatorExperience, locale),
  },
  {
    code: "03",
    label: locale === "fr" ? "Exécution" : "Execution",
    value:
      locale === "fr"
        ? "Stratégie, architecture, mise en œuvre, évaluation et transfert restent connectés du cadrage à la production."
        : "Strategy, architecture, implementation, evaluation, and handoff stay connected from scope to production.",
  },
  {
    code: "04",
    label: locale === "fr" ? "Expertise" : "Expertise",
    value:
      locale === "fr"
        ? "IA, données, automatisation de processus et systèmes décisionnels sont traités comme une seule capacité opérationnelle."
        : "AI, data, workflow automation, and decision-system design are treated as one operating capability.",
  },
  {
    code: "05",
    label: locale === "fr" ? "Marché" : "Market",
    value: localize(siteContent.brand.geography, locale),
  },
];

export const localizeProofText = (text: LocalizedText, locale: Locale) => localize(text, locale);
